/**
 * Состояние ленты вложений: controlled `modelValue`, tombstone, единый commit.
 * Нативные `File` восстанавливаются через `attachmentNativeFileRegistry` (фаза E).
 */
import type { FileItem } from '@/common/types/fileItem'
import { computed, ref, watch, type Ref } from 'vue'
import {
  attachmentIncomingMatchesTombstone,
  attachmentItemsReferSameRow,
  findMatchingAttachmentItem,
} from '@/common/utils/fileAttachmentsUniData'
import {
  logAttachmentStoreDebug,
  summarizeAttachmentFileItems,
} from '@/common/utils/attachmentDebugLog'

export type AttachmentTapeCommitSource = 'user' | 'store'

export type UseAttachmentTapeStateOptions = {
  modelValue: Ref<FileItem[]>
  emitUpdate: (items: FileItem[]) => void
  normalizeIncoming: (item: unknown) => FileItem
  onTapeChanged?: (items: FileItem[]) => void
  onRebindPreview?: () => void
}

export function useAttachmentTapeState(options: UseAttachmentTapeStateOptions) {
  const tombstonedItems = ref<FileItem[]>([])

  function tombstoneAttachmentItem(item: FileItem): void {
    if (tombstonedItems.value.some((t) => attachmentItemsReferSameRow(t, item))) {
      return
    }
    tombstonedItems.value = [...tombstonedItems.value, item]
  }

  function filterTombstonedIncoming(items: FileItem[]): FileItem[] {
    if (tombstonedItems.value.length === 0) return items
    return items.filter(
      (item) => !tombstonedItems.value.some((t) => attachmentIncomingMatchesTombstone(item, t)),
    )
  }

  function normalizeTape(items: unknown[]): FileItem[] {
    return items.map((item) => options.normalizeIncoming(item))
  }

  const tapeItems = computed((): FileItem[] => {
    const raw = options.modelValue.value
    if (!Array.isArray(raw)) return []
    return filterTombstonedIncoming(normalizeTape(raw))
  })

  function commitTapeUpdate(next: FileItem[], source: AttachmentTapeCommitSource): void {
    const normalized = normalizeTape(next)
    const incoming = source === 'store' ? filterTombstonedIncoming(normalized) : normalized
    if (source === 'user') {
      options.emitUpdate(incoming)
    }
    options.onTapeChanged?.(incoming)
    options.onRebindPreview?.()
  }

  function removeTapeRow(target: FileItem): FileItem[] {
    return tapeItems.value.filter((item) => !attachmentItemsReferSameRow(item, target))
  }

  function appendTapeRows(rows: FileItem[]): FileItem[] {
    return [...tapeItems.value, ...rows]
  }

  function resolveRowInTape(needle: FileItem): FileItem | null {
    return findMatchingAttachmentItem(tapeItems.value, needle) ?? null
  }

  watch(
    () => options.modelValue.value,
    (next) => {
      const items = Array.isArray(next) ? normalizeTape(next) : []
      logAttachmentStoreDebug('tape-modelValue', {
        source: 'watch',
        ...summarizeAttachmentFileItems(items),
      })
      options.onRebindPreview?.()
    },
    { deep: true },
  )

  return {
    tapeItems,
    tombstonedItems,
    tombstoneAttachmentItem,
    filterTombstonedIncoming,
    commitTapeUpdate,
    removeTapeRow,
    appendTapeRows,
    resolveRowInTape,
  }
}
